import { useEffect, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import type { AuthenticationOutcome } from '@ayumia/contracts';
import { formatRoute } from '../app/router';
import type { AuthClient } from '../api/auth-client';
import { ClearableInput, PasswordInput, SubmitButton } from '../design-system/fields';
import { Icon } from '../design-system/icons';
import { FormMessage } from '../design-system/FormMessage';
import { authFailure } from './auth-errors';
import { readRememberedEmail, rememberEmail } from './remembered-email';
import type { SignInPresentationBody } from '@ayumia/contracts';
import { text } from '../app/i18n';
import { backgroundOf, backgroundStyle, brandAsset, DEFAULT_SIGN_IN_PRESENTATION } from './sign-in-presentation';
import { SIGNIN_LOCALES, SIGNIN_TEXTS, type SignInLocale } from './signin-i18n';

/**
 * Connexion (ADR-091) : adresse et mot de passe, puis le second facteur si le compte l'a activé,
 * ou son enrôlement si un rôle l'exige. Aucun champ de tenant : l'adresse désigne une identité,
 * l'identité porte le tenant. Les messages d'échec ne disent pas si l'adresse existe.
 */

type Step =
  | { kind: 'credentials' }
  | { kind: 'totp'; challenge: string }
  | { kind: 'enroll'; challenge: string; secret?: string; uri?: string }
  | { kind: 'recovery_codes'; codes: string[]; outcome: AuthenticationOutcome }
  | { kind: 'forgot'; sent: boolean };


function LanguagePicker({ locales, locale, onChange, label }: { locales: readonly SignInLocale[]; locale: SignInLocale; onChange: (locale: SignInLocale) => void; label: string }) {
  return (
    <div className="ay-signin__languages" role="group" aria-label={label}>
      {SIGNIN_LOCALES.filter((entry) => locales.includes(entry.code)).map((entry) => (
        <button key={entry.code} type="button" lang={entry.code} aria-pressed={entry.code === locale} title={entry.name} onClick={() => onChange(entry.code)}>
          {entry.label}
        </button>
      ))}
    </div>
  );
}

const HELP_URL = /^https:\/\/[^\s"'<>]+$/;

/**
 * Carte de connexion (habillage v1, ADR-092 §7) : fond (image de marque, jeton de couleur ou
 * rien), logo de marque sur en-tête blanc, titre, sous-titre et pied de page selon la
 * présentation publique. Les images sont des actifs de `public/brand/` (un jeton n'admet pas
 * d'URL) ; la couleur vient d'un jeton sémantique, jamais d'une valeur libre.
 */
export function AuthCard({
  title,
  description,
  children,
  aside,
  presentation = DEFAULT_SIGN_IN_PRESENTATION,
  locale = 'fr',
}: {
  title: string;
  description?: string;
  children: ReactNode;
  aside?: ReactNode;
  presentation?: SignInPresentationBody;
  locale?: SignInLocale;
}) {
  const background = backgroundOf(presentation);
  const style = backgroundStyle(background) as CSSProperties;
  const logo = brandAsset(presentation.logo);
  const footer = presentation.footer;
  const help = footer?.help_url !== undefined && HELP_URL.test(footer.help_url) ? footer.help_url : undefined;
  return (
    <main className={`ay-signin ay-signin--${background.kind}`} style={style} lang={locale}>
      {aside}
      <section className="ay-signin__card" aria-labelledby="ay-signin-title">
        <div className="ay-signin__logo">{logo === undefined ? <span className="ay-signin__wordmark">AYUMIA</span> : <img src={logo} alt="AYUMIA" />}</div>
        <div className="ay-signin__body">
          <h1 id="ay-signin-title" className="ay-signin__title">
            {title}
          </h1>
          {description !== undefined && <p className="ay-signin__description">{description}</p>}
          {children}
          {(footer?.text !== undefined || help !== undefined) && (
            <p className="ay-signin__footer">
              {footer?.text !== undefined && <span>{text(footer.text, locale)}</span>}{' '}
              {help !== undefined && (
                <a href={help} target="_blank" rel="noopener noreferrer">
                  {text(footer?.help_label, locale, help)}
                </a>
              )}
            </p>
          )}
        </div>
      </section>
    </main>
  );
}

/** Bandeau d'erreur du design system (`.ay-alert--danger`), annoncé (`role="alert"`). */
export function FormError({ message }: { message: string | null }) {
  return message === null ? null : (
    <p className="ay-alert ay-alert--danger" role="alert">
      <Icon name="alert" size={16} />
      <span>{message}</span>
    </p>
  );
}

export function SignInPage({
  client,
  onSignedIn,
  loadPresentation,
}: {
  client: AuthClient;
  onSignedIn: (outcome: AuthenticationOutcome) => void;
  /** Habillage public (ADR-092 §7) ; absent ou en échec : le défaut de plateforme. */
  loadPresentation?: () => Promise<SignInPresentationBody>;
}) {
  const [step, setStep] = useState<Step>({ kind: 'credentials' });
  const [presentation, setPresentation] = useState<SignInPresentationBody>(DEFAULT_SIGN_IN_PRESENTATION);
  const [locale, setLocale] = useState<SignInLocale>(DEFAULT_SIGN_IN_PRESENTATION.default_locale);
  const chosenLocale = useRef(false);
  useEffect(() => {
    if (loadPresentation === undefined) return;
    let live = true;
    loadPresentation()
      .catch(() => DEFAULT_SIGN_IN_PRESENTATION)
      .then((loaded) => {
        if (!live) return;
        setPresentation(loaded);
        if (!chosenLocale.current) setLocale(loaded.default_locale);
        if (!loaded.remember_me) {
          // Option désactivée : l'adresse mémorisée n'est ni proposée ni réécrite.
          setRemember(false);
          setEmail((current) => (current === readRememberedEmail() ? '' : current));
        }
      });
    return () => {
      live = false;
    };
  }, [loadPresentation]);
  const chooseLocale = (next: SignInLocale): void => {
    chosenLocale.current = true;
    setLocale(next);
  };
  const t = SIGNIN_TEXTS[locale];
  const [email, setEmail] = useState(readRememberedEmail);
  const [remember, setRemember] = useState(() => readRememberedEmail() !== '');
  const passwordField = useRef<HTMLInputElement | null>(null);
  const emailField = useRef<HTMLInputElement | null>(null);
  // Adresse mémorisée : le focus va au mot de passe, sinon à l'adresse.
  useEffect(() => {
    if (step.kind !== 'credentials') return;
    (email !== '' ? passwordField.current : emailField.current)?.focus();
    // Seulement à l'arrivée sur l'étape, pas à chaque frappe : l'adresse n'est pas une dépendance.
  }, [step.kind]);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  // L'échec est gardé tel quel et traduit au rendu : changer de langue traduit aussi le message.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (work: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      await work();
    } catch (caught) {
      setFailure({ error: caught });
    } finally {
      setBusy(false);
    }
  };

  const after = (outcome: AuthenticationOutcome): void => {
    setPassword('');
    setCode('');
    if (outcome.status === 'authenticated') onSignedIn(outcome);
    else if (outcome.status === 'mfa_required' && outcome.challenge !== undefined) setStep({ kind: 'totp', challenge: outcome.challenge });
    else if (outcome.status === 'mfa_enrollment_required' && outcome.challenge !== undefined) setStep({ kind: 'enroll', challenge: outcome.challenge });
    // ADR-093 : mot de passe correct mais à changer (hérité du v1, hors politique) ; aucune session,
    // `challenge` est un lien de réinitialisation à usage unique, consommé par la page de lien.
    else if (outcome.status === 'password_change_required' && outcome.challenge !== undefined) window.location.hash = `#/password/reset?token=${outcome.challenge}`;
  };

  const submit = (work: () => Promise<void>) => (event: FormEvent): void => {
    event.preventDefault();
    void run(work);
  };

  const shown = failure === null ? null : authFailure(failure.error, locale, t);
  const message = <FormMessage message={shown?.text ?? null} detail={shown?.detail} detailLabel={t.details} />;
  const picker = presentation.show_locale_switcher && presentation.locales.length > 1 ? <LanguagePicker locales={presentation.locales} locale={locale} onChange={chooseLocale} label={t.language} /> : undefined;
  const card = (title: string, children: ReactNode, description?: string) => (
    <AuthCard title={title} presentation={presentation} locale={locale} {...(picker === undefined ? {} : { aside: picker })} {...(description === undefined ? {} : { description })}>
      {children}
    </AuthCard>
  );
  const back = (
    <button type="button" className="ay-signin__link" onClick={() => setStep({ kind: 'credentials' })}>
      {t.back}
    </button>
  );

  if (step.kind === 'totp') {
    return card(
      t.totpTitle,
      <form className="ay-form ay-form--touch" onSubmit={submit(async () => after(await client.verifyTotp(step.challenge, code.trim())))}>
        <p className="ay-text--muted">{t.totpHelp}</p>
        <label className="ay-field ay-signin__field">
          <span className="ay-field__label">{t.code}</span>
          <input name="code" inputMode="numeric" autoComplete="one-time-code" placeholder="123456" autoFocus required value={code} onChange={(event) => setCode(event.target.value)} />
        </label>
        <div className="ay-form__submit">
          <SubmitButton busy={busy} busyLabel={t.verifying} className="ay-button--strong">
            {t.verify}
          </SubmitButton>
        </div>
        {message}
        {back}
      </form>,
    );
  }

  if (step.kind === 'enroll') {
    return card(
      t.enrollTitle,
      <div className="ay-form ay-form--touch">
        <p className="ay-text--muted">{t.enrollHelp}</p>
        {step.secret === undefined ? (
          <>
            <button
              type="button"
              className="ay-button ay-button--lg ay-button--block ay-button--strong"
              disabled={busy}
              aria-busy={busy}
              onClick={() =>
                void run(async () => {
                  const outcome = await client.startEnrollment({ challenge: step.challenge });
                  if (outcome.totp !== undefined) setStep({ ...step, secret: outcome.totp.secret, uri: outcome.totp.otpauth_uri });
                })
              }
            >
              {busy && <span className="ay-spinner" aria-hidden="true" />}
              <span>{t.showKey}</span>
            </button>
            {message}
          </>
        ) : (
          <form
            className="ay-form ay-form--touch"
            onSubmit={submit(async () => {
              const outcome = await client.confirmEnrollment(code.trim(), step.challenge);
              setCode('');
              setStep({ kind: 'recovery_codes', codes: outcome.recovery_codes ?? [], outcome });
            })}
          >
            <p>
              {t.keyLabel} <code className="ay-signin__secret">{step.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
            </p>
            <p className="ay-text--muted">
              {t.openLink} <a href={step.uri}>{t.addAccount}</a>
            </p>
            <label className="ay-field ay-signin__field">
              <span className="ay-field__label">{t.shownCode}</span>
              <input name="code" inputMode="numeric" autoComplete="one-time-code" placeholder="123456" required value={code} onChange={(event) => setCode(event.target.value)} />
            </label>
            <div className="ay-form__submit">
              <SubmitButton busy={busy} busyLabel={t.verifying} className="ay-button--strong">
                {t.activate}
              </SubmitButton>
            </div>
            {message}
          </form>
        )}
      </div>,
    );
  }

  if (step.kind === 'recovery_codes') {
    return card(
      t.recoveryTitle,
      <div className="ay-form">
        <p>{t.recoveryHelp}</p>
        <ul className="ay-signin__codes" aria-label={t.recoveryTitle}>
          {step.codes.map((recovery) => (
            <li key={recovery}>
              <code>{recovery}</code>
            </li>
          ))}
        </ul>
        <button type="button" className="ay-button ay-button--lg ay-button--block ay-button--strong" onClick={() => onSignedIn(step.outcome)}>
          {t.recoveryKept}
        </button>
      </div>,
    );
  }

  if (step.kind === 'forgot') {
    return card(
      t.forgotTitle,
      step.sent ? (
        <div className="ay-form">
          <p role="status">{t.forgotSent}</p>
          {back}
        </div>
      ) : (
        <form
          className="ay-form ay-form--touch"
          onSubmit={submit(async () => {
            await client.requestReset(email.trim());
            setStep({ kind: 'forgot', sent: true });
          })}
        >
          <label className="ay-field ay-signin__field">
            <span className="ay-field__label">{t.email}</span>
            <ClearableInput type="email" name="email" autoComplete="username" placeholder={t.emailPlaceholder} required value={email} onValueChange={setEmail} clearLabel={t.clearEmail} />
          </label>
          <div className="ay-form__submit">
            <SubmitButton busy={busy} busyLabel={t.sending} className="ay-button--strong">
              {t.getLink}
            </SubmitButton>
          </div>
          {message}
          {back}
        </form>
      ),
    );
  }

  return card(
    text(presentation.title, locale, t.title),
    <form
      className="ay-form ay-form--touch"
      onSubmit={submit(async () => {
        const address = email.trim();
        // « Se souvenir de moi » : l'adresse seulement, jamais le mot de passe ni le jeton.
        if (presentation.remember_me) rememberEmail(remember && address !== '' ? address : null);
        after(await client.login(address, password));
      })}
    >
      <label className="ay-field ay-signin__field">
        <span className="ay-field__label">{t.email}</span>
        <ClearableInput ref={emailField} type="email" name="email" autoComplete="username" placeholder={t.emailPlaceholder} required value={email} onValueChange={setEmail} clearLabel={t.clearEmail} />
      </label>
      <label className="ay-field ay-signin__field">
        <span className="ay-field__label">{t.password}</span>
        <PasswordInput ref={passwordField} name="password" autoComplete="current-password" placeholder={t.passwordPlaceholder} required value={password} onValueChange={setPassword} showLabel={t.showPassword} hideLabel={t.hidePassword} />
      </label>
      {(presentation.remember_me || presentation.forgot_password) && (
        <div className="ay-signin__options">
          {presentation.remember_me && (
            <label className="ay-checkbox ay-signin__remember">
              <input type="checkbox" checked={remember} onChange={(event) => setRemember(event.target.checked)} />
              <span>{t.remember}</span>
            </label>
          )}
          {presentation.forgot_password && (
            <button type="button" className="ay-signin__link" onClick={() => setStep({ kind: 'forgot', sent: false })}>
              {t.forgot}
            </button>
          )}
        </div>
      )}
      <div className="ay-form__submit">
        <SubmitButton busy={busy} busyLabel={t.signingIn} className="ay-button--strong">
          {t.submit}
        </SubmitButton>
      </div>
      {message}
      {presentation.booking_link.enabled && (
        <p className="ay-signin__booking">
          {t.noAccount} <a href={formatRoute({ kind: 'booking' })}>{t.booking}</a>
        </p>
      )}
    </form>,
    presentation.subtitle === undefined ? undefined : text(presentation.subtitle, locale),
  );
}
